<template>
  <LoaderComponent />
  <ChangePasswordComponent />

  <aside class="cl-sidebar" :class="{ 'cl-sidebar--collapsed': collapsed }">

    <!-- Brand row -->
    <div class="cl-sidebar__brand">
      <template v-if="!collapsed">
        <div class="cl-sidebar__logo">
          <img src="@/assets/images/pera.png" alt="Clementine" class="cl-sidebar__logo-img" />
          <span class="cl-sidebar__logo-text">
            Clementine
            <span class="badge bg-info rounded-pill ms-1 cl-host-badge" v-if="isHost()">{{ $t('host') }}</span>
          </span>
        </div>
      </template>
      <template v-else>
        <img src="@/assets/images/pera.png" alt="Clementine" class="cl-sidebar__logo-img" />
      </template>
    </div>

    <!-- Navigation -->
    <nav class="cl-sidebar__nav">
      <ul class="cl-nav">

        <!-- Static: Home -->
        <li class="cl-nav__item">
          <router-link to="/home" class="cl-nav__link" :class="{ 'cl-nav__link--active': $route.path === '/home' }"
            :title="$t(buildKey('Home'))">
            <i class="fas fa-home cl-nav__icon"></i>
            <span class="cl-nav__label">{{ $t(buildKey('Home')) }}</span>
          </router-link>
        </li>

        <!-- Dynamic 3-level menu -->
        <template v-for="(page, idx) in data" :key="idx">

          <!-- Level 1: leaf (no children) -->
          <li class="cl-nav__item" v-if="hasNotChild(page)">
            <router-link :to="page.pageUrl" class="cl-nav__link"
              :class="{ 'cl-nav__link--active': hasActive(page, $route.path) }"
              :title="$t(buildKey(page.pageName))">
              <i :class="makeIcon(page)" class="cl-nav__icon"></i>
              <span class="cl-nav__label">{{ $t(buildKey(page.pageName)) }}</span>
            </router-link>
          </li>

          <!-- Level 1: group (has children) -->
          <li class="cl-nav__item" v-else>
            <button class="cl-nav__link cl-nav__link--btn w-100"
              :class="{ 'cl-nav__link--active': isGroupActive(page, $route.path) }"
              :title="$t(buildKey(page.pageName))"
              @click="onParentGroupClick(idx)">
              <i :class="makeIcon(page)" class="cl-nav__icon"></i>
              <span class="cl-nav__label">{{ $t(buildKey(page.pageName)) }}</span>
              <i class="fas cl-nav__chevron"
                :class="openGroups.includes(idx) ? 'fa-angle-left' : 'fa-angle-right'"></i>
            </button>

            <!-- Level 2 children -->
            <ul v-show="openGroups.includes(idx)" class="cl-nav cl-nav--children">

              <template v-for="(child, cidx) in page.childs" :key="cidx">

                <!-- Level 2: no URL, no children (header label) -->
                <li class="cl-nav__item" v-if="hasNotChildPageURL(child)">
                  <span class="cl-nav__link cl-nav__link--child cl-nav__link--disabled"
                    :title="$t(buildKey(child.pageName))">
                    <span class="cl-nav__bullet">•</span>
                    <span class="cl-nav__label">{{ $t(buildKey(child.pageName)) }}</span>
                  </span>
                </li>

                <!-- Level 2: has URL, no children (leaf) -->
                <li class="cl-nav__item" v-else-if="hasPageURLNotChild(child)">
                  <router-link :to="child.pageUrl" class="cl-nav__link cl-nav__link--child"
                    :class="{ 'cl-nav__link--active': hasActive(child, $route.path) }"
                    :title="$t(buildKey(child.pageName))">
                    <span class="cl-nav__bullet">•</span>
                    <span class="cl-nav__label">{{ $t(buildKey(child.pageName)) }}</span>
                  </router-link>
                </li>

                <!-- Level 2: has grandchildren -->
                <li class="cl-nav__item" v-else>
                  <button class="cl-nav__link cl-nav__link--child cl-nav__link--btn w-100"
                    :class="{ 'cl-nav__link--active': isGroupActive(child, $route.path) }"
                    :title="$t(buildKey(child.pageName))"
                    @click="toggleGroup(`${idx}-${cidx}`)">
                    <span class="cl-nav__bullet">•</span>
                    <span class="cl-nav__label">{{ $t(buildKey(child.pageName)) }}</span>
                    <i class="fas cl-nav__chevron"
                      :class="openGroups.includes(`${idx}-${cidx}`) ? 'fa-angle-left' : 'fa-angle-right'"></i>
                  </button>

                  <!-- Level 3 grandchildren -->
                  <ul v-show="openGroups.includes(`${idx}-${cidx}`)" class="cl-nav cl-nav--grandchildren">
                    <li v-for="(grand, gidx) in child.childs" :key="gidx" class="cl-nav__item">
                      <router-link :to="grand.pageUrl" class="cl-nav__link cl-nav__link--grand"
                        :class="{ 'cl-nav__link--active': hasActive(grand, $route.path) }"
                        :title="$t(buildKey(grand.pageName))">
                        <span class="cl-nav__bullet cl-nav__bullet--sm">›</span>
                        <span class="cl-nav__label">{{ $t(buildKey(grand.pageName)) }}</span>
                      </router-link>
                    </li>
                  </ul>
                </li>

              </template>
            </ul>
          </li>

        </template>
      </ul>
    </nav>

    <!-- Footer -->
    <div class="cl-sidebar__footer">
      Copyright &copy;<a href="#">Numberskills</a>. All rights reserved.
    </div>

  </aside>
</template>

<script lang="ts">
import agent, { masterServiceBaseURL } from '@/services/agent';
import authService from '@/services/authService';
import { useDataSyncConfigurationStore } from '@/store/useDataSyncConfigurationStore';
import { useUserStore } from '@/store/useUserStore';
import { defineComponent } from 'vue';
import ChangePasswordComponent from '../master/features/ChangePassword/ChangePasswordComponent.vue';
import LoaderComponent from '../molecules/LoaderComponent.vue';
import emitter from '@/services/mitt';
import { UserPage } from './model';
import { signOutFromMicrosoft } from '@/services/msalHelper';

export default defineComponent({
  components: {
    LoaderComponent,
    ChangePasswordComponent,
  },
  setup() {
    const userStore = useUserStore();
    const dataSyncConfigurationStore = useDataSyncConfigurationStore();
    return { userStore, dataSyncConfigurationStore };
  },
  created() {
    agent.setBaseUrl(masterServiceBaseURL);
  },
  mounted() {
    this.loadUserPage();
    emitter.on('update-permission', () => {
      this.loadUserPage();
    });
  },
  data() {
    return {
      data: [] as UserPage[],
      collapsed: false,
      openGroups: [] as (number | string)[],
    };
  },
  methods: {
    toggleSidebar() {
      this.collapsed = !this.collapsed;
    },
    toggleGroup(key: number | string) {
      const i = this.openGroups.indexOf(key);
      if (i === -1) this.openGroups.push(key);
      else this.openGroups.splice(i, 1);
    },
    isGroupActive(page: UserPage, path: string): boolean {
      if (!page.childs) return false;
      return page.childs.some(c =>
        c.pageUrl === path ||
        (c.childs && c.childs.some(g => g.pageUrl === path))
      );
    },
    async loadUserPage() {
      const user = authService.getJwtUser();
      this.userStore.setLoggedInUser(user);
      if (user) {
        await this.userStore.ensurePagesLoaded(true);
        this.data = this.userStore.userPages;
        // Auto-open the group that contains the active route
        this.data.forEach((page, idx) => {
          if (this.isGroupActive(page, this.$route.path)) {
            if (!this.openGroups.includes(idx)) this.openGroups.push(idx);
          }
        });
      }
    },
    isHost() {
      return authService.isHost();
    },
    buildKey(inputString: string) {
      const words = inputString.split(' ');
      return words.map((word: string, index: number) => {
        if (index === 0) return word.toLowerCase();
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      }).join('');
    },
    hasNotChildPageURL(page: UserPage) {
      return !page.pageUrl && (!page.childs || page.childs.length === 0);
    },
    hasPageURLNotChild(page: UserPage) {
      return page.pageUrl && (!page.childs || page.childs.length === 0);
    },
    hasNotChild(page: UserPage) {
      return !page.childs || page.childs.length === 0;
    },
    hasChilds(page: UserPage) {
      return page && page.childs && page.childs.length > 0;
    },
    hasActive(page: UserPage, path: string) {
      return path === page.pageUrl;
    },
    makeIcon(page: UserPage) {
      return page.pageIcon || 'fas fa-circle';
    },
    onParentGroupClick(idx: any) {
  if (this.collapsed) {
    this.collapsed = false;
    if (!this.openGroups.includes(idx)) {
      this.openGroups.push(idx);
    }
  } else {
    this.toggleGroup(idx);
  }
}
  },
});
</script>